import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ActiveCoverage, ActiveCoverageOption, ActiveCoverageService, filterCoverageOptions } from '@lib/insurance-packages/data-access';
import { ClinicClassSummaryComponent, CoverageCategoriesComponent, CoverageLimitsComponent, DiagnosesCountBadgeComponent } from '@lib/insurance-packages/ui';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { LoaderComponent } from '@lib/shared-ui/components/layout';

import { MemberCoverageDiagnosesComponent } from '../member-coverage-diagnoses/member-coverage-diagnoses.component';

interface OptionDiagnosesState {
  data: string[];
  loading: boolean;
  error: boolean;
}

@Component({
  selector: 'tasks-member-coverage-options',
  standalone: true,
  imports: [
    MatIcon,
    MatTooltip,
    TranslatePipe,
    LoaderComponent,
    MemberCoverageDiagnosesComponent,
    CoverageLimitsComponent,
    ClinicClassSummaryComponent,
    CoverageCategoriesComponent,
    DiagnosesCountBadgeComponent,
  ],
  templateUrl: './member-coverage-options.component.html',
  host: { class: 'flex min-h-0 flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberCoverageOptionsComponent {
  private readonly activeCoverageService = inject(ActiveCoverageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly coverage = input.required<ActiveCoverage>();

  protected readonly search = signal<string>('');
  private readonly collapsedOptions = signal<Record<string, boolean>>({});
  private readonly expandedCategories = signal<Record<string, boolean>>({});

  protected readonly visibleOptions = computed<ActiveCoverageOption[]>(() => filterCoverageOptions(this.coverage().options ?? [], this.search()));

  protected readonly diagnosesOption = signal<ActiveCoverageOption | null>(null);
  protected readonly diagnosesSearch = signal<string>('');
  private readonly optionDiagnoses = signal<Record<string, OptionDiagnosesState>>({});
  private readonly currentDiagnoses = computed(() => {
    const optionId = this.diagnosesOption()?.id;
    return optionId ? (this.optionDiagnoses()[optionId] ?? null) : null;
  });
  protected readonly diagnosesLoading = computed<boolean>(() => this.currentDiagnoses()?.loading ?? true);
  protected readonly diagnosesError = computed<boolean>(() => this.currentDiagnoses()?.error ?? false);
  protected readonly diagnoses = computed<string[]>(() => this.currentDiagnoses()?.data ?? []);

  protected openDiagnoses(option: ActiveCoverageOption): void {
    this.diagnosesOption.set(option);
    this.diagnosesSearch.set('');
    const diagnoses = this.currentDiagnoses();
    if (!diagnoses || diagnoses.error) this.loadDiagnoses();
  }

  protected loadDiagnoses(): void {
    const coverageId = this.coverage().id;
    const optionId = this.diagnosesOption()?.id;
    if (!optionId) return;

    this.setOptionDiagnoses(optionId, { data: [], loading: true, error: false });
    this.activeCoverageService
      .getOptionDiagnoses(coverageId, optionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => this.setOptionDiagnoses(optionId, { data, loading: false, error: false }),
        error: () => this.setOptionDiagnoses(optionId, { data: [], loading: false, error: true }),
      });
  }

  protected closeDiagnoses(): void {
    this.diagnosesOption.set(null);
  }

  protected isOptionCollapsed(id: string): boolean {
    return !this.search().trim() && this.collapsedOptions()[id] === true;
  }

  protected toggleOption(id: string): void {
    this.collapsedOptions.update(current => ({ ...current, [id]: !current[id] }));
  }

  protected areCategoriesExpanded(id: string): boolean {
    return this.expandedCategories()[id] === true;
  }

  protected toggleCategories(id: string): void {
    this.expandedCategories.update(current => ({ ...current, [id]: !current[id] }));
  }

  private setOptionDiagnoses(optionId: string, state: OptionDiagnosesState): void {
    this.optionDiagnoses.update(current => ({ ...current, [optionId]: state }));
  }
}
